import AgentHubAccountMenu from 'agentHub/components/AgentHubAccountMenu'
import AgentHubBookingOnboardingNeedHelpButton from 'agentHub/onboarding/AgentHubBookingOnboardingNeedHelpButton'
import AgentHubCustomerViewSwitch from 'agentHub/components/AgentHubCustomerViewSwitch'
import { isAgentHubCVFREnabled } from 'agentHub/selectors/isAgentHubCVFREnabled'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import clsx from 'clsx'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useRef } from 'react'
import styled from 'styled-components'
import NavBarBrandLogo from './NavBar/NavBarBrandLogo'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import NavBarNotificationsButton from './NavBarButtons/NavBarNotificationsButton'
import NavBarUserButton from './NavBarButtons/NavBarUserButton'
import NavBarRegionButton from './NavBarButtons/NavBarRegionButton'
import NavBarSearchInput from './NavBar/NavBarSearchInput'
import NavBarDrawerButton from './NavBarButtons/NavBarDrawerButton'
import NavBarTripButton from './NavBarButtons/NavBarTripButton'
import NavBarLoginButton from './NavBarButtons/NavBarLoginButton'
import NavBarCartButton from './NavBarButtons/NavBarCartButton'
import NavBarSearchButton from './NavBarButtons/NavBarSearchButton'
import NavBarMyEscapesButton from './NavBarButtons/NavBarMyEscapesButton'
import useIsHomepage from 'home/hooks/isHomepage'
import NavBarSearch from './NavBarSearch/NavBarSearch'
import LoyaltyRewardsNotification from 'loyalty/components/LoyaltyRewardsNotification'

const BarPane = styled(Pane)`
  width: 100%;
`

const UtilBar = styled(LayoutContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${rem(64)};
  padding-inline: ${rem(4)} ${rem(16)};

  &.logged-in {
    padding-inline-end: ${rem(8)};
  }

  ${mediaQueryDown.mobile} {
    &.logged-in {
      justify-content: flex-start;
      gap: ${rem(8)};
    }
  }

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: 1fr minmax(1px, ${rem(1140)}) 1fr;
    gap: ${rem(8)};

    &.logged-in {
      padding-inline-end: ${rem(12)};
    }
  }

  ${mediaQueryUp.desktop} {
    gap: ${rem(32)};
  }

  ${mediaQueryUp.largeDesktop} {
    padding-inline: ${rem(20)} ${rem(32)};
    gap: ${rem(64)};

    &.logged-in {
      padding-inline-end: ${rem(20)};
    }
  }
`

const UtilSearchBar = styled(LayoutContainer)`
  padding: 0 ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const UtilBarStart = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
  flex-shrink: 0;

  ${mediaQueryUp.desktop} {
    gap: ${rem(8)};
    flex-grow: 1;
  }
`

const UtilBarCenter = styled.div`
  min-width: 0;
  overflow: hidden;

  ${mediaQueryDown.mobile} {
    display: none;
  }
`

const UtilBarEnd = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem(8)};
  flex-shrink: 0;

  ${mediaQueryDown.mobile} {
    gap: ${rem(4)};

    ${UtilBar}.logged-in & {
      flex: 1 1 0;
      min-width: 0;
    }
  }
`

const NavBarUtilBarSearchInput = styled(NavBarSearchInput)`
  display: none;
  min-width: 0;
  max-width: 100%;

  ${mediaQueryUp.tablet} {
    display: flex;
  }
`

const NavBarUtilRegionButton = styled(NavBarRegionButton)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
  }
`

const NavBarUtilMyEscapes = styled(NavBarMyEscapesButton)`
  flex-shrink: 0;
`

const NavBarUtilSearch = styled(NavBarSearch)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const NavBarUtilLoginButton = styled(NavBarLoginButton)`
  /** Login button requires extra spacing gap alone cannot provide */
  margin-inline-start: ${rem(8)};
`

const UserButtonWrapper = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    min-width: 0;
    overflow: visible;
  }
`

function NavBarUtilBar() {
  const searchInputRef = useRef<HTMLButtonElement>(null)
  const loggedIn = useAppSelector(selectLoggedIn)
  const isHomepage = useIsHomepage()
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const showMyEscapes = !isAgentHubEnabled || isAgentHubCVFREnabled || isSpoofed
  const showRegionButton = !isAgentHubEnabled
  const showCustomerAccount = !isAgentHubEnabled

  return <BarPane>
    <UtilBar
      variant="extraLarge"
      className={clsx({ 'logged-in': loggedIn })}
      data-util-bar
    >
      <UtilBarStart data-util-bar-start>
        <NavBarDrawerButton />
        <NavBarBrandLogo {...(isAgentHubEnabled ? { 'data-agent-hub-onboarding': 'welcome' } : {})} />
      </UtilBarStart>
      <UtilBarCenter data-util-bar-center>
        <NavBarUtilBarSearchInput
          ref={searchInputRef}
          {...(isAgentHubEnabled ? { 'data-agent-hub-onboarding': 'search' } : {})}
        />
      </UtilBarCenter>
      <UtilBarEnd data-util-bar-end>
        <NavBarSearchButton />
        {showRegionButton && <NavBarUtilRegionButton />}
        <NavBarTripButton />
        {!loggedIn && <NavBarUtilLoginButton />}
        {loggedIn && <>
          {isAgentHubEnabled && <AgentHubBookingOnboardingNeedHelpButton />}
          {isAgentHubEnabled && <AgentHubCustomerViewSwitch />}
          <NavBarNotificationsButton />
          <NavBarCartButton />
          {showMyEscapes && <NavBarUtilMyEscapes />}
          {isAgentHubEnabled && <AgentHubAccountMenu />}
          {showCustomerAccount && <UserButtonWrapper>
            <NavBarUserButton />
            <LoyaltyRewardsNotification />
          </UserButtonWrapper>}
        </>}
      </UtilBarEnd>
    </UtilBar>
    {isHomepage && <UtilSearchBar variant="extraLarge">
      <NavBarSearchInput />
    </UtilSearchBar>}
    <NavBarUtilSearch
      searchInputRef={searchInputRef}
    />
  </BarPane>
}

export default NavBarUtilBar
